// src/pages/dashboard/branch/categories/CategoryDetailPage.tsx
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiCoffee,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiLayers,
  FiMove,
  FiMoreVertical,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";

import Badge from "../../../../components/ui/Badge";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/Card";
import Dropdown from "../../../../components/ui/Dropdown";
import Skeleton from "../../../../components/ui/Skeleton";
import { getCategoriesByBranch } from "../../../../services/categoryService";
import {
  getCategoryDescription,
  getCategoryName,
} from "../../../../utils/categoryHelper";
import { formatDate } from "../../../../utils/formatters";
import { Category, MenuItem, getBranchMenuItems, batchUpdateCategorySortOrders, batchUpdateItemSortOrders } from "../../../../utils/supabaseClient";
import usePagination from "../../../../hooks/usePagination";
import MenuItemListCard from "../menu-items/components/MenuItemListCard";
import { Database } from "../../../../types/supabase";
import AddCategoryModal from "./components/AddCategoryModal";
import DeleteCategoryModal from "./components/DeleteCategoryModal";
import UpdateCategoryModal from "./components/UpdateCategoryModal";
import AddMenuItemModal from "../menu-items/components/add-item-modal/AddMenuItemModal";
import { useAppDispatch } from "../../../../store/hooks";
import { setHasChanges } from "../../../../store/slices/branchSlice";
import { updateCategoryData } from "../../../../store/slices/categorySlice";

const CategoryDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isRTL = i18n.language === "ar";

  const [isLoading, setIsLoading] = useState(true);
  const [category, setCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteSubcategoryModalOpen, setIsDeleteSubcategoryModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isAddSubcategoryModalOpen, setIsAddSubcategoryModalOpen] =
    useState(false);
  const [activeTab, setActiveTab] = useState<"subcategories" | "items">("subcategories");
  const [subcategoryToDelete, setSubcategoryToDelete] =
    useState<Category | null>(null);
  const [subcategoryToUpdate, setSubcategoryToUpdate] =
    useState<Category | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [isSavingSubcategoryOrder, setIsSavingSubcategoryOrder] = useState(false);
  const [isSavingItemOrder, setIsSavingItemOrder] = useState(false);

  const { branchId, categoryId } = useParams<{
    branchId: string;
    categoryId: string;
  }>();

  const itemsPagination = usePagination({
    initialPage: 1,
    initialLimit: 12
  });

  const getSortOrderValue = (value?: number | null) =>
    Number.isFinite(value) ? (value as number) : Number.MAX_SAFE_INTEGER;

  const compareBySortOrder = <T extends { sort_order?: number | null }>(a: T, b: T) => {
    const diff = getSortOrderValue(a.sort_order) - getSortOrderValue(b.sort_order);
    if (diff !== 0) return diff;

    return String((a as { id?: string }).id ?? '').localeCompare(String((b as { id?: string }).id ?? ''));
  };

  const getDescendantCategoryIds = (rootId: string, allCategories: Category[]) => {
    const descendants: string[] = [];
    const stack = [rootId];

    while (stack.length > 0) {
      const currentId = stack.pop()!;
      const directChildren = allCategories
        .filter((cat) => cat.parent_id === currentId)
        .map((cat) => cat.id);

      descendants.push(...directChildren);
      stack.push(...directChildren);
    }

    return descendants;
  };

  const fetchMenuItems = async (categoryId: string, branchId: string) => {
    if (!categoryId || !branchId) return;

    setItemsLoading(true);
    try {
      const currentCategory = categories.find((cat) => cat.id === categoryId);
      const isMainCategoryView = !currentCategory?.parent_id;

      const allItemsResult = await getBranchMenuItems(branchId);
      if (allItemsResult.error || !allItemsResult.data) {
        setMenuItems([]);
        return;
      }

      const relevantCategoryIds = new Set<string>([categoryId]);
      if (isMainCategoryView) {
        const descendantIds = getDescendantCategoryIds(categoryId, categories);
        descendantIds.forEach((id) => relevantCategoryIds.add(id));
      }

      const filteredItems = allItemsResult.data.filter(
        (item) =>
          !!item.category_id &&
          relevantCategoryIds.has(item.category_id) &&
          item.is_available &&
          item.is_published
      );

      const subcategoryOrderMap = new Map<string, number>();
      if (isMainCategoryView) {
        const sortedSubcategories = categories
          .filter((cat) => cat.parent_id === categoryId)
          .sort(compareBySortOrder);

        sortedSubcategories.forEach((subcat, index) => {
          subcategoryOrderMap.set(subcat.id, index);

          const descendantIds = getDescendantCategoryIds(subcat.id, categories);
          descendantIds.forEach((descendantId) => {
            if (!subcategoryOrderMap.has(descendantId)) {
              subcategoryOrderMap.set(descendantId, index);
            }
          });
        });
      }

      const sortedItems = [...filteredItems].sort((a, b) => {
        if (isMainCategoryView) {
          const aCategoryKey = a.category_id ?? '';
          const bCategoryKey = b.category_id ?? '';
          const aSubcategoryOrder = subcategoryOrderMap.get(aCategoryKey) ?? Number.MAX_SAFE_INTEGER;
          const bSubcategoryOrder = subcategoryOrderMap.get(bCategoryKey) ?? Number.MAX_SAFE_INTEGER;
          if (aSubcategoryOrder !== bSubcategoryOrder) {
            return aSubcategoryOrder - bSubcategoryOrder;
          }
        }

        const manualOrderDiff = getSortOrderValue(a.sort_order) - getSortOrderValue(b.sort_order);
        if (manualOrderDiff !== 0) return manualOrderDiff;

        const createdAtDiff = new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime();
        if (createdAtDiff !== 0) return createdAtDiff;

        return String(a.id ?? '').localeCompare(String(b.id ?? ''));
      });

      const start = (itemsPagination.currentPage - 1) * itemsPagination.limit;
      const end = start + itemsPagination.limit;
      setMenuItems(sortedItems.slice(start, end));
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setItemsLoading(false);
    }
  };

  const fetchData = async (categoryId: string, branchId: string) => {
    if (!categoryId || !branchId) return;

    setIsLoading(true);
    try {
      const categoriesData = await getCategoriesByBranch(branchId);
      if (categoriesData) {
        const categoryData = categoriesData.find(
          (cat) => cat.id === categoryId
        );
        if (!categoryData) throw new Error("category not found!");

        setCategory(categoryData);
        setCategories(categoriesData);

        // Get subcategories for this category
        const subcats = categoriesData.filter(
          (cat) => cat.parent_id === categoryId
        );
        setSubcategories([...subcats].sort(compareBySortOrder));
      }
    } catch (error) {
      console.error("Error fetching category details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(categoryId!, branchId!);
  }, [categoryId, branchId]);

  useEffect(() => {
    if (categoryId && branchId && categories.length > 0) {
      fetchMenuItems(categoryId, branchId);
    }
  }, [categoryId, branchId, categories, itemsPagination.currentPage, itemsPagination.limit]);

  useEffect(() => {
    if (category && category.parent_id && activeTab !== 'items') {
      setActiveTab('items');
    }
  }, [category, activeTab]);

  const handleSubcategoryDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const prev = [...subcategories];
    const reordered = [...subcategories];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setSubcategories(reordered);
    setIsSavingSubcategoryOrder(true);

    try {
      await batchUpdateCategorySortOrders(
        reordered.map((sub, idx) => ({ id: sub.id, sort_order: idx }))
      );
      dispatch(setHasChanges(true));
    } catch (error) {
      console.error('Error reordering subcategories:', error);
      setSubcategories(prev);
    } finally {
      setIsSavingSubcategoryOrder(false);
    }
  };

  const handleItemsDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const prev = [...menuItems];
    const reordered = [...menuItems];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setMenuItems(reordered);
    setIsSavingItemOrder(true);

    try {
      await batchUpdateItemSortOrders(
        reordered.map((item, idx) => ({ id: item.id, sort_order: idx }))
      );
      dispatch(setHasChanges(true));
    } catch (error) {
      console.error('Error reordering category items:', error);
      setMenuItems(prev);
    } finally {
      setIsSavingItemOrder(false);
    }
  };

  const getItemCategoryName = (categoryIdValue: string | null) => {
    if (!categoryIdValue) return '-';
    const cat = categories.find((catItem) => catItem.id === categoryIdValue);
    return cat ? getCategoryName(cat, i18n.language) : '-';
  };

  type UpdateCategoryPayload = Database["public"]["Functions"]["update_category"]["Args"]["payload"];

  const togglePublishStatus = async () => {
    if (!category) return;

    try {
      const payload = {
        id: category.id,
        is_published: !category.is_published,
      } as UpdateCategoryPayload;

      const updatedCategory = await dispatch(
        updateCategoryData(payload)
      ).unwrap();

      setCategory(updatedCategory);
      setCategories((prev) =>
        prev.map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat))
      );
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  const toggleSubcategoryPublishStatus = async (subcategory: Category) => {
    try {
      const payload = {
        id: subcategory.id,
        is_published: !subcategory.is_published,
      } as UpdateCategoryPayload;

      const updatedSubcategory = await dispatch(
        updateCategoryData(payload)
      ).unwrap();

      setSubcategories((prev) =>
        prev.map((sub) => (sub.id === updatedSubcategory.id ? updatedSubcategory : sub))
      );
      setCategories((prev) =>
        prev.map((cat) => (cat.id === updatedSubcategory.id ? updatedSubcategory : cat))
      );
    } catch (error) {
      console.error("Error updating subcategory:", error);
    }
  };

  const handleSubcategoryClick = (subcategory: Category) => {
    navigate(`/dashboard/${branchId}/categories/${subcategory.id}`);
  };

  const handleSubcategoryEdit = (subcategory: Category) => {
    setSubcategoryToUpdate(subcategory);
    setIsUpdateModalOpen(true);
  };

  const handleSubcategoryDelete = (subcategory: Category) => {
    setSubcategoryToDelete(subcategory);
    setIsDeleteSubcategoryModalOpen(true);
  };

  // Check if this is a main category (no parent) or subcategory
  const isMainCategory = !category?.parent_id;
  const hasSubcategories = subcategories.length > 0;

  type ItemTableRow =
    | { type: 'header'; key: string; title: string }
    | { type: 'item'; key: string; item: MenuItem; dragIndex: number };

  const itemTableRows = useMemo<ItemTableRow[]>(() => {
    if (!isMainCategory) {
      return menuItems.map((item, index) => ({
        type: 'item',
        key: item.id,
        item,
        dragIndex: index,
      }));
    }

    const grouped = new Map<string, MenuItem[]>();
    menuItems.forEach((item) => {
      const groupKey = item.category_id ?? 'uncategorized';
      const existing = grouped.get(groupKey) ?? [];
      existing.push(item);
      grouped.set(groupKey, existing);
    });

    const rows: ItemTableRow[] = [];
    let dragIndex = 0;

    grouped.forEach((items, categoryId) => {
      const categoryTitle = getItemCategoryName(categoryId === 'uncategorized' ? null : categoryId);
      rows.push({
        type: 'header',
        key: `header-${categoryId}`,
        title: categoryTitle,
      });

      items.forEach((item) => {
        rows.push({
          type: 'item',
          key: item.id,
          item,
          dragIndex,
        });
        dragIndex += 1;
      });
    });

    return rows;
  }, [isMainCategory, menuItems, categories, i18n.language]);

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <Skeleton
            width="w-8"
            height="h-8"
            rounded="rounded-full"
            className="me-3"
          />
          <Skeleton width="w-40" height="h-8" />
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
              <div className="mb-4 sm:mb-0">
                <Skeleton width="w-64" height="h-8" className="mb-2" />
                <Skeleton width="w-96" height="h-4" />
              </div>
              <div className="flex gap-3">
                <Skeleton width="w-24" height="h-10" rounded="rounded-md" />
                <Skeleton width="w-24" height="h-10" rounded="rounded-md" />
                <Skeleton width="w-24" height="h-10" rounded="rounded-md" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Skeleton width="w-full" height="h-64" className="mb-6" />
              </div>
              <div>
                <Skeleton width="w-full" height="h-12" className="mb-3" />
                <Skeleton width="w-full" height="h-8" className="mb-3" />
                <Skeleton width="w-3/4" height="h-8" className="mb-3" />
                <Skeleton width="w-1/2" height="h-8" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
            {t("categories.notFound")}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t("categories.notFoundDescription")}
          </p>
          <Link to={`/dashboard/${branchId}/categories`}>
            <Button leftIcon={isRTL ? <FiChevronRight /> : <FiChevronLeft />}>
              {t("categories.backToCategories")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20 sm:pb-8">
      {/* Header */}
      <div className="mb-4">
        <Link
          to={`/dashboard/${branchId}/categories`}
          className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-dark"
        >
          {isRTL ? (
            <FiChevronRight className="ms-2 h-4 w-4" />
          ) : (
            <FiArrowLeft className="me-2 h-4 w-4" />
          )}
          {t("categories.backToCategories")}
        </Link>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="p-4 sm:p-6">
          {/* Category Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
            <div className="flex-1 min-w-0">
              {/* <Card className="overflow-hidden border-none shadow-lg"> */}
              <div className="relative rounded-lg overflow-hidden">
                {/* Background Image or Gradient */}
                {category.image_url ? (
                  <div className="relative h-48 sm:h-64 overflow-hidden">
                    <img
                      src={category.image_url}
                      alt={getCategoryName(category, i18n.language)}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  </div>
                ) : (
                  <div className="relative h-48 sm:h-64 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <FiLayers className="w-16 h-16 sm:w-24 sm:h-24 text-gray-400" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                )}

                {/* Content Overlay */}
                <div className="absolute inset-0 bg-black/50 p-4 sm:p-6">
                  <div className="h-full flex flex-col justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant={category.is_published ? "success" : "gray"}
                            className={
                              category.is_published
                                ? "bg-green-500/20 border-green-400/30 text-green-100"
                                : "bg-gray-500/20 border-gray-400/30 text-gray-100"
                            }
                          >
                            {category.is_published ? (
                              <FiEye className="w-3 h-3 me-1" />
                            ) : (
                              <FiEyeOff className="w-3 h-3 me-1" />
                            )}
                            {category.is_published
                              ? t("categories.published")
                              : t("categories.draft")}
                          </Badge>
                          {/* <Badge variant="primary" className="bg-blue-500/20 border-blue-400/30 text-blue-100">
													{isMainCategory ? t('categories.mainCategory') : t('categories.subcategory')}
												</Badge> */}
                        </div>

                        {/* Desktop Actions */}
                        <div className="hidden sm:flex gap-3 flex-wrap">
                          <Button
                            variant="outline"
                            className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
                            leftIcon={
                              category.is_published ? <FiEyeOff /> : <FiEye />
                            }
                            onClick={togglePublishStatus}
                            size="sm"
                          >
                            <span className="inline">
                              {category.is_published
                                ? t("categories.unpublish")
                                : t("categories.publish")}
                            </span>
                          </Button>

                          <Button
                            className="bg-primary hover:bg-primary-dark"
                            leftIcon={<FiEdit2 />}
                            onClick={() => setIsUpdateModalOpen(true)}
                            size="sm"
                          >
                            <span className="inline">{t("common.edit")}</span>
                          </Button>

                          <Button
                            variant="outline"
                            className="bg-red-500/10 border-red-400/20 text-red-400 hover:bg-red-500/20"
                            leftIcon={<FiTrash2 />}
                            onClick={() => setIsDeleteModalOpen(true)}
                            size="sm"
                          >
                            <span className="inline">{t("common.delete")}</span>
                          </Button>
                        </div>

                        {/* Mobile Actions Dropdown */}
                        <div className="sm:hidden">
                          <Dropdown
                            placement="bottom-end"
                            trigger={
                              <Button
                                variant="outline"
                                size="sm"
                                rightIcon={<FiMoreVertical />}
                                className="!px-3"
                              >
                                {t("common.actions")}
                              </Button>
                            }
                            items={[
                              {
                                label: category.is_published
                                  ? t("categories.unpublish")
                                  : t("categories.publish"),
                                icon: category.is_published ? FiEyeOff : FiEye,
                                onClick: () => togglePublishStatus(),
                              },
                              {
                                label: t("common.edit"),
                                icon: FiEdit2,
                                onClick: () => setIsUpdateModalOpen(true),
                              },
                              {
                                label: t("common.delete"),
                                icon: FiTrash2,
                                onClick: () => setIsDeleteModalOpen(true),
                                className: "text-red-600 hover:text-red-700",
                              },
                            ]}
                          />
                        </div>
                      </div>

                      <h2 className="text-2xl sm:text-3xl font-bold text-white">
                        {getCategoryName(category, i18n.language)}
                      </h2>

                      {getCategoryDescription(category, i18n.language) && (
                        <p className="text-gray-200 text-sm sm:text-base line-clamp-2 sm:line-clamp-4">
                          {getCategoryDescription(category, i18n.language)}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-gray-200 text-sm">
                      <span className="flex items-center gap-2">
                        <FiCalendar className="w-4 h-4" />
                        {formatDate(category.updated_at ?? '', i18n.language)}
                      </span>
                      {isMainCategory && (
                        <span className="flex items-center gap-2">
                          <FiLayers className="w-4 h-4" />
                          {t("categories.itemCount", {
                            count: subcategories.length,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* </Card> */}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              {/* Tabbed Content - Subcategories and Menu Items */}
              <Card>
                {/* Tab Navigation */}
                <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                  <nav className="flex space-x-4 -mb-px">
                    {/* Subcategories Tab */}
                    {isMainCategory && (
                      <button
                        onClick={() => setActiveTab("subcategories")}
                        className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm cursor-pointer transition-colors ${activeTab === "subcategories"
                          ? "border-primary text-primary"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                          }`}
                      >
                        {t("categories.subcategories")}
                      </button>
                    )}

                    {/* Menu Items Tab */}
                    <button
                      onClick={() => setActiveTab("items")}
                      className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm cursor-pointer transition-colors ${activeTab === "items"
                        ? "border-primary text-primary"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                        }`}
                    >
                      {t("categories.menuItems")}
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Subcategories Tab Content */}
                  {activeTab === "subcategories" && isMainCategory && (
                    <>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("categories.itemCount", { count: subcategories.length })}
                          </p>
                          {isSavingSubcategoryOrder && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 animate-pulse">
                              {t('settings.savingOrder')}
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          leftIcon={<FiPlus />}
                          onClick={() => setIsAddSubcategoryModalOpen(true)}
                          className="hidden sm:flex"
                        >
                          {t("categories.addSubcategory")}
                        </Button>
                      </div>

                      {hasSubcategories ? (
                        <>
                          <div className="px-4 py-2.5 rounded-lg bg-primary/10 dark:bg-primary/15 border border-primary/20 flex items-center gap-2 text-sm text-primary font-medium mb-4">
                            <FiMove className="w-4 h-4 flex-shrink-0" />
                            <span>{t('settings.dragToReorder')}</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="px-3 py-3 w-10" />
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {t('categories.name')}
                                  </th>
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {t('categories.description')}
                                  </th>
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {t('categories.status')}
                                  </th>
                                  <th className="relative px-6 py-3">
                                    <span className="sr-only">{t('common.actions')}</span>
                                  </th>
                                </tr>
                              </thead>
                              <DragDropContext onDragEnd={handleSubcategoryDragEnd}>
                                <Droppable droppableId="subcategories-list-detail">
                                  {(provided) => (
                                    <tbody
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                      className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                                    >
                                      {subcategories.map((subcategory, index) => (
                                        <Draggable key={subcategory.id} draggableId={subcategory.id} index={index}>
                                          {(provided, snapshot) => (
                                            <tr
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              className={`transition-colors ${snapshot.isDragging ? 'bg-primary/5 dark:bg-primary/10 shadow-lg' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                                            >
                                              <td className="px-3 py-4 w-10">
                                                <div
                                                  {...provided.dragHandleProps}
                                                  className="cursor-grab active:cursor-grabbing text-primary hover:text-primary/80 transition-colors p-1.5 rounded-md bg-primary/10 hover:bg-primary/20"
                                                  title={t('settings.dragToReorder')}
                                                >
                                                  <FiMove className="w-4 h-4" />
                                                </div>
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center gap-3">
                                                  {subcategory.image_url && (
                                                    <img
                                                      src={subcategory.image_url}
                                                      alt={getCategoryName(subcategory, i18n.language)}
                                                      className="h-8 w-8 rounded object-cover"
                                                    />
                                                  )}
                                                  <button
                                                    onClick={() => handleSubcategoryClick(subcategory)}
                                                    className="text-start hover:text-primary transition-colors cursor-pointer"
                                                  >
                                                    {getCategoryName(subcategory, i18n.language)}
                                                  </button>
                                                </div>
                                              </td>
                                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                                {getCategoryDescription(subcategory, i18n.language) || (
                                                  <span className="text-gray-400 dark:text-gray-500 italic">
                                                    {t('categories.noDescription')}
                                                  </span>
                                                )}
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {subcategory.is_published ? (
                                                  <Badge variant="success">{t('categories.published')}</Badge>
                                                ) : (
                                                  <Badge variant="gray">{t('categories.draft')}</Badge>
                                                )}
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                  <button
                                                    onClick={() => handleSubcategoryEdit(subcategory)}
                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
                                                  >
                                                    <FiEdit2 className="w-5 h-5" />
                                                  </button>
                                                  <button
                                                    onClick={() => handleSubcategoryDelete(subcategory)}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
                                                  >
                                                    <FiTrash2 className="w-5 h-5" />
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </tbody>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <FiLayers className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                            {t("categories.noSubcategories")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {t("categories.addSubcategoryDescription")}
                          </p>
                          <div className="mt-6">
                            <Button
                              onClick={() => setIsAddSubcategoryModalOpen(true)}
                              leftIcon={<FiPlus />}
                            >
                              {t("categories.addFirstSubcategory")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Menu Items Tab Content */}
                  {activeTab === "items" && (
                    <>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("categories.menuItemsCount", { count: menuItems.length })}
                          </p>
                          {isSavingItemOrder && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 animate-pulse">
                              {t('settings.savingOrder')}
                            </span>
                          )}
                        </div>
                        <Link to={`/dashboard/${branchId}/menu?category=${category?.id}`}>
                          <Button
                            size="sm"
                            variant="outline"
                            rightIcon={isRTL ? <FiChevronLeft /> : <FiChevronRight />}
                          >
                            {t("categories.viewAll")}
                          </Button>
                        </Link>
                      </div>

                      {itemsLoading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                          {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                              <Skeleton height="h-40" width="w-full" />
                              <div className="p-4">
                                <Skeleton height="h-6" width="w-3/4" className="mb-3" />
                                <Skeleton height="h-4" width="w-full" className="mb-2" />
                                <Skeleton height="h-4" width="w-5/6" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : menuItems.length === 0 ? (
                        <div className="text-center py-12">
                          <FiCoffee className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                            {t("categories.noMenuItems")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {t("categories.noMenuItemsDescription")}
                          </p>
                          <div className="mt-6">
                            <Button
                              leftIcon={<FiPlus />}
                              onClick={() => setIsAddItemModalOpen(true)}
                            >
                              {t("categories.addFirstMenuItem")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="px-4 py-2.5 rounded-lg bg-primary/10 dark:bg-primary/15 border border-primary/20 flex items-center gap-2 text-sm text-primary font-medium mb-4">
                            <FiMove className="w-4 h-4 flex-shrink-0" />
                            <span>{t('settings.dragToReorder')}</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="px-3 py-3 w-10" />
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/5">
                                    {t('menuItems.name')}
                                  </th>
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
                                    {t('menuItems.category')}
                                  </th>
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                                    {t('menuItems.price')}
                                  </th>
                                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
                                    {t('menuItems.status')}
                                  </th>
                                  <th className="relative px-6 py-3 w-24">
                                    <span className="sr-only">{t('common.actions')}</span>
                                  </th>
                                </tr>
                              </thead>
                              <DragDropContext onDragEnd={handleItemsDragEnd}>
                                <Droppable droppableId="category-items-list-detail">
                                  {(provided) => (
                                    <tbody
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                      className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                                    >
                                      {itemTableRows.map((row) => {
                                        if (row.type === 'header') {
                                          return (
                                            <tr key={row.key} className="bg-primary/5 dark:bg-primary/10">
                                              <td colSpan={6} className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-primary">
                                                {row.title}
                                              </td>
                                            </tr>
                                          );
                                        }

                                        return (
                                          <Draggable key={row.key} draggableId={row.item.id} index={row.dragIndex}>
                                            {(provided, snapshot) => (
                                              <tr
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={`transition-colors ${snapshot.isDragging ? 'bg-primary/5 dark:bg-primary/10 shadow-lg' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                                              >
                                                <td className="px-3 py-4 w-10">
                                                  <div
                                                    {...provided.dragHandleProps}
                                                    className="cursor-grab active:cursor-grabbing text-primary hover:text-primary/80 transition-colors p-1.5 rounded-md bg-primary/10 hover:bg-primary/20"
                                                    title={t('settings.dragToReorder')}
                                                  >
                                                    <FiMove className="w-4 h-4" />
                                                  </div>
                                                </td>
                                                <MenuItemListCard
                                                  item={row.item}
                                                  handleEditClick={() => navigate(`/dashboard/${branchId}/menu`)}
                                                  handleDeleteClick={() => navigate(`/dashboard/${branchId}/menu`)}
                                                  onPublishToggle={() => {
                                                    if (categoryId && branchId) {
                                                      fetchMenuItems(categoryId, branchId);
                                                    }
                                                  }}
                                                  categoryName={getItemCategoryName(row.item.category_id || null)}
                                                  asCells
                                                />
                                              </tr>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                      {provided.placeholder}
                                    </tbody>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            </table>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </motion.div>
              </Card>
            </div>

            {/* Sidebar - Category Info */}
            <div>
              <Card>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t("categories.categoryDetails")}
                </h3>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t("categories.categoryType")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {isMainCategory
                        ? t("categories.mainCategory")
                        : t("categories.subcategory")}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t("categories.status")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {category.is_published
                        ? t("categories.published")
                        : t("categories.draft")}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t("categories.lastUpdated")}
                    </h4>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {formatDate(category.updated_at ?? '', i18n.language)}
                    </p>
                  </div>

                  {isMainCategory && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t("categories.subcategories")}
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {t("categories.itemCount", {
                          count: subcategories.length,
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Floating Add Button */}
      {isMainCategory && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="fixed bottom-16 end-6 sm:hidden z-50"
        >
          <Button
            size="lg"
            onClick={() => setIsAddSubcategoryModalOpen(true)}
            className="!rounded-full !w-14 !h-14 !p-0 shadow-lg"
          >
            <FiPlus className="w-6 h-6" />
          </Button>
        </motion.div>
      )}

      {/* Modals */}
      <UpdateCategoryModal
        isOpen={isUpdateModalOpen && !subcategoryToUpdate}
        category={category}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSubcategoryToUpdate(null);
        }}
        parentCategories={categories.filter((cat) => !cat.parent_id)}
        onCategoryUpdated={(updatedCategory) => {
          // Update the current category state
          setCategory(updatedCategory);

          // If parent_id changed, we need to refetch to get the updated category structure
          if (updatedCategory.parent_id !== category.parent_id) {
            fetchData(categoryId!, branchId!);
          }

          setSubcategoryToUpdate(null);
        }}
        branchId={branchId!}
      />

      <UpdateCategoryModal
        isOpen={isUpdateModalOpen && !!subcategoryToUpdate}
        category={subcategoryToUpdate!}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSubcategoryToUpdate(null);
        }}
        parentCategories={categories.filter((cat) => !cat.parent_id)}
        onCategoryUpdated={(updatedCategory) => {
          // Update the subcategory in the list
          setSubcategories(prev =>
            prev.map(sub => sub.id === updatedCategory.id ? updatedCategory : sub)
          );

          // If parent_id changed, the subcategory might no longer belong to this category
          if (updatedCategory.parent_id !== subcategoryToUpdate?.parent_id) {
            if (updatedCategory.parent_id !== categoryId) {
              // Subcategory moved to different parent, remove from current list
              setSubcategories(prev => prev.filter(sub => sub.id !== updatedCategory.id));
            }
          }

          setSubcategoryToUpdate(null);
        }}
        branchId={branchId!}
      />

      <DeleteCategoryModal
        isOpen={isDeleteModalOpen}
        category={category}
        onClose={() => setIsDeleteModalOpen(false)}
        onCategoryDeleted={(deletedCategory) => {
          setCategories(
            categories.filter((cat) => cat.id != deletedCategory?.id)
          );
          navigate(`/dashboard/${branchId}/categories`);
        }}
      />

      <DeleteCategoryModal
        isOpen={isDeleteSubcategoryModalOpen && !!subcategoryToDelete}
        category={subcategoryToDelete!}
        onClose={() => {
          setIsDeleteSubcategoryModalOpen(false);
          setSubcategoryToDelete(null);
        }}
        onCategoryDeleted={(deletedCategory) => {
          // Remove the deleted subcategory from the local state
          setSubcategories(prev =>
            prev.filter((cat) => cat.id !== deletedCategory?.id)
          );
          // Close the modal and reset state
          setIsDeleteSubcategoryModalOpen(false);
          setSubcategoryToDelete(null);
        }}
      />

      <AddCategoryModal
        onClose={() => setIsAddSubcategoryModalOpen(false)}
        isOpen={isAddSubcategoryModalOpen}
        branchId={branchId!}
        mainCategories={[category]}
        onCategoryAdded={(newCategory) => {
          // Add the new subcategory to the list without refetching
          setSubcategories(prev => [...prev, newCategory].sort(compareBySortOrder));
          setCategories(prev => [...prev, newCategory].sort(compareBySortOrder));
          setIsAddSubcategoryModalOpen(false);
        }}
        selectedParentId={category.id}
      />

      <AddMenuItemModal
        isOpen={isAddItemModalOpen}
        onClose={() => setIsAddItemModalOpen(false)}
        onMenuItemAdded={() => {
          setIsAddItemModalOpen(false);
          // Refresh menu items
          if (categoryId && branchId) {
            fetchMenuItems(categoryId, branchId);
          }
        }}
        branchId={branchId!}
        categories={categories}
        selectedCategoryId={categoryId}
      />
    </div>
  );
};

export default CategoryDetailPage;
